import Link from "next/link";
import {
  ArrowRight,
  Building2,
  UserRound,
  Briefcase,
  Sparkles,
  CheckCircle2,
  Users,
  ShieldCheck,
  Zap,
  GraduationCap,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import LandingPageShell from "@/components/landing-page/layout/LandingPageShell";
import Reveal from "@/components/ui/Reveal";
import { BOOK_DEMO_HREF, CONTACT_MAIL } from "@/lib/marketing-links";

type PathCard = {
  id: string;
  icon: LucideIcon;
  badge: string;
  isPopular?: boolean;
  title: string;
  tagline: string;
  description: string;
  features: readonly string[];
  audience: readonly string[];
  primaryCta: { label: string; href: string };
  secondaryCta?: { label: string; href: string; external?: boolean };
  footerNote?: { text: string; linkText: string; href: string };
};

const cards: readonly PathCard[] = [
  {
    id: "individuals",
    icon: UserRound,
    badge: "Self-Paced Learning",
    title: "For Individuals",
    tagline: "Build practical AI skills for your work and career.",
    description:
      "Explore personalized learning paths designed around your role, expertise, and goals, from AI fundamentals to advanced production workflows.",
    features: [
      "Personalized learning tracks tailored to your skill level",
      "Interactive hands-on practice & scenario checkpoints",
      "Verifiable career certificates upon completion",
    ],
    audience: ["Professionals", "Engineers", "Leaders", "Career Switchers"],
    primaryCta: {
      label: "Explore Individual Learning",
      href: "/catalog",
    },
    footerNote: {
      text: "Already have an account?",
      linkText: "Sign in",
      href: "/sign-in",
    },
  },
  {
    id: "organizations",
    icon: Building2,
    badge: "⭐ Self-Serve Workspace",
    isPopular: true,
    title: "For Teams & Organizations",
    tagline: "Deploy AI capability across your workforce.",
    description:
      "Instantly provision a dedicated enterprise workspace. Onboard departments with corporate domain verification, assign curated tracks, and monitor team analytics.",
    features: [
      "Instant self-serve setup (no credit card required)",
      "Corporate email domain restriction (@company.com)",
      "Manager progress dashboards & cohort tracking",
    ],
    audience: ["Tech Leads", "L&D Managers", "HR Leaders", "Team Admins"],
    primaryCta: {
      label: "Register Organization Workspace",
      href: "/b2b/register",
    },
    secondaryCta: {
      label: "Explore B2B Solutions",
      href: "/b2b",
    },
  },
  {
    id: "enterprise",
    icon: Briefcase,
    badge: "Custom & Scaled",
    title: "Enterprise & Custom",
    tagline: "Tailored AI transformation for large enterprises.",
    description:
      "Partner with our learning architects to co-create proprietary curricula mapped directly to your internal tech stack, security policies, and workforce objectives.",
    features: [
      "Custom course authoring for proprietary workflows",
      "Dedicated enterprise LMS & private LLM integration",
      "Dedicated L&D strategist & executive onboarding",
    ],
    audience: ["Enterprises (500+)", "Chief AI Officers", "HR Directors", "Procurement"],
    primaryCta: {
      label: "Book a Free Consultation",
      href: BOOK_DEMO_HREF,
    },
    secondaryCta: {
      label: "Contact Enterprise Team",
      href: `mailto:${CONTACT_MAIL}`,
      external: true,
    },
  },
];

const TRUST_FACTS = [
  {
    icon: Zap,
    title: "Instant Provisioning",
    description: "Launch your workspace in under 2 minutes",
  },
  {
    icon: ShieldCheck,
    title: "Domain Isolation",
    description: "Strict corporate email verification",
  },
  {
    icon: Sparkles,
    title: "Adaptive Learning",
    description: "Diagnostic placements & personalized pacing",
  },
  {
    icon: GraduationCap,
    title: "Verified Credentials",
    description: "Recognized certificates of completion",
  },
] as const;

export default function GetStartedPage() {
  return (
    <LandingPageShell surface="white">
      {/* Background ambient lighting */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(0,80,239,0.08),transparent_55%)]"
      />

      <section
        aria-labelledby="get-started-heading"
        className="min-h-[calc(100svh_-_5rem)] flex flex-col justify-center px-margin-mobile md:px-margin-desktop py-size-xl relative z-10"
      >
        <div className="w-full max-w-7xl mx-auto">
          {/* Header */}
          <Reveal className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-mkt-primary-container/[0.08] border border-mkt-primary/20 text-mkt-primary text-xs font-semibold uppercase tracking-wider mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Get Started with inaraX</span>
            </div>
            <h1
              id="get-started-heading"
              className="font-display-lg text-[clamp(32px,4.2vw,56px)] leading-[1.15] tracking-[-0.02em] font-bold text-mkt-inarax-black text-balance"
            >
              How would you like to learn with inaraX?
            </h1>
            <p className="mt-size-sm font-body-lg text-body-lg text-mkt-on-surface-variant text-balance max-w-2xl mx-auto">
              Choose the path that fits your goals. From individual career acceleration to self-serve team workspaces and tailored enterprise enablement.
            </p>
          </Reveal>

          {/* Cards Grid */}
          <ul className="list-none pl-0 ml-0 mt-size-lg grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {cards.map((card, index) => {
              const {
                id,
                icon: Icon,
                badge,
                isPopular,
                title,
                tagline,
                description,
                features,
                audience,
                primaryCta,
                secondaryCta,
                footerNote,
              } = card;

              return (
                <li key={id} className="flex">
                  <Reveal delayMs={index === 0 ? undefined : (index * 100 as 100 | 200)} className="w-full flex flex-col">
                    <div
                      className={`relative flex flex-col h-full rounded-3xl p-6 sm:p-8 transition-all duration-300 hover:-translate-y-1.5 ${
                        isPopular
                          ? "bg-gradient-to-b from-white via-indigo-50/[0.3] to-white border-2 border-mkt-primary/40 shadow-[0_12px_36px_rgba(0,60,185,0.14)] hover:shadow-[0_20px_48px_rgba(0,60,185,0.20)] hover:border-mkt-primary"
                          : "bg-white border border-slate-200/90 shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:border-slate-300 hover:shadow-[0_12px_32px_rgba(0,60,185,0.10)]"
                      }`}
                    >
                      {/* Top Badge */}
                      <div className="flex items-center justify-between mb-5 gap-2">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${
                            isPopular
                              ? "bg-mkt-primary text-white shadow-xs"
                              : "bg-slate-100 text-slate-700 border border-slate-200/80"
                          }`}
                        >
                          {badge}
                        </span>

                        <span
                          aria-hidden="true"
                          className={`flex h-11 w-11 items-center justify-center rounded-2xl ${
                            isPopular
                              ? "bg-mkt-primary text-white shadow-sm shadow-mkt-primary/20"
                              : "bg-mkt-primary-container/[0.08] text-mkt-primary-container"
                          }`}
                        >
                          <Icon className="h-5 w-5" />
                        </span>
                      </div>

                      {/* Title & Tagline */}
                      <h2
                        id={`${id}-heading`}
                        className="font-label-md text-xs uppercase tracking-widest text-mkt-primary font-bold"
                      >
                        {title}
                      </h2>
                      <p className="mt-1 font-headline-md text-xl sm:text-2xl font-bold text-mkt-inarax-black leading-snug">
                        {tagline}
                      </p>
                      <p className="mt-3 text-sm text-mkt-on-surface-variant leading-relaxed">
                        {description}
                      </p>

                      {/* Key Features Bullet List */}
                      <div className="mt-5 pt-5 border-t border-slate-100 space-y-2.5">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                          Highlights:
                        </p>
                        <ul className="space-y-2 pl-0 list-none">
                          {features.map((feature) => (
                            <li key={feature} className="flex items-start gap-2.5 text-xs text-slate-700 leading-normal">
                              <CheckCircle2
                                className={`w-4 h-4 shrink-0 mt-0.5 ${
                                  isPopular ? "text-mkt-primary" : "text-emerald-600"
                                }`}
                              />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Audience Tags */}
                      <div className="mt-5 pt-5 border-t border-slate-100">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                          Designed For:
                        </p>
                        <ul className="list-none pl-0 ml-0 flex flex-wrap gap-1.5">
                          {audience.map((who) => (
                            <li
                              key={who}
                              className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700"
                            >
                              {who}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* CTA Area */}
                      <div className="mt-auto pt-6 flex flex-col gap-2.5">
                        <Link
                          href={primaryCta.href}
                          aria-describedby={`${id}-heading`}
                          className={`group w-full inline-flex items-center justify-center gap-2 font-semibold text-sm py-3 px-5 rounded-xl transition-all duration-200 cursor-pointer ${
                            isPopular
                              ? "bg-mkt-primary text-white shadow-md shadow-mkt-primary/25 hover:bg-mkt-primary/90 hover:scale-[1.02] active:scale-[0.98]"
                              : "bg-slate-900 text-white hover:bg-slate-800 hover:scale-[1.01] active:scale-[0.98]"
                          }`}
                        >
                          <span>{primaryCta.label}</span>
                          <ArrowRight
                            aria-hidden="true"
                            className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                          />
                        </Link>

                        {secondaryCta && (
                          <Link
                            href={secondaryCta.href}
                            {...(secondaryCta.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                            className="w-full inline-flex items-center justify-center gap-1.5 font-medium text-xs py-2.5 px-4 rounded-xl text-slate-700 hover:text-mkt-primary hover:bg-slate-100/80 transition-colors"
                          >
                            <span>{secondaryCta.label}</span>
                            <ChevronRight className="w-3.5 h-3.5" />
                          </Link>
                        )}

                        {footerNote && (
                          <p className="text-center text-xs text-mkt-on-surface-variant pt-1">
                            {footerNote.text}{" "}
                            <Link
                              href={footerNote.href}
                              className="text-mkt-primary font-semibold underline underline-offset-2 hover:opacity-80"
                            >
                              {footerNote.linkText}
                            </Link>
                          </p>
                        )}
                      </div>
                    </div>
                  </Reveal>
                </li>
              );
            })}
          </ul>

          {/* Employee / Team Member Quick Access Callout */}
          <Reveal delayMs={300} className="mt-size-md">
            <div className="rounded-3xl border border-slate-200/90 bg-slate-50/70 p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
              <div className="flex items-start gap-4 max-w-2xl">
                <div className="p-3 bg-mkt-primary-container/[0.1] text-mkt-primary-container rounded-2xl shrink-0 mt-0.5">
                  <Users className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-900">
                    Already part of an organization workspace?
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 mt-1 leading-relaxed">
                    If your employer or university already has an inaraX workspace, you can sign in directly with your corporate email address or accept an invite link sent by your administrator.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 shrink-0 w-full md:w-auto">
                <Link
                  href="/sign-in"
                  className="w-full sm:w-auto text-center px-5 py-2.5 rounded-xl bg-white border border-slate-300/80 hover:border-slate-400 text-slate-800 text-xs font-semibold shadow-xs hover:bg-slate-50 transition"
                >
                  Sign In with Work Email
                </Link>
                <Link
                  href="/b2b/register"
                  className="w-full sm:w-auto text-center px-5 py-2.5 rounded-xl bg-mkt-primary text-white text-xs font-semibold shadow-sm hover:bg-mkt-primary/90 transition flex items-center justify-center gap-1.5"
                >
                  <Building2 className="w-3.5 h-3.5" />
                  Register Organization
                </Link>
              </div>
            </div>
          </Reveal>

          {/* Trust & Platform Value Strip */}
          <Reveal delayMs={300} className="mt-size-lg pt-size-md border-t border-slate-200/80">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {TRUST_FACTS.map((item) => {
                const ItemIcon = item.icon;
                return (
                  <div key={item.title} className="flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-slate-100 text-mkt-primary shrink-0 mt-0.5">
                      <ItemIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-900">{item.title}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>
    </LandingPageShell>
  );
}
